import React from 'react';
import { Code, Cloud, Brain, Wrench, Database } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const Services = () => {
  const services = [
    {
      icon: Code,
      title: 'IT Outsourcing',
      description: 'Comprehensive IT outsourcing services with skilled developers in Java, Python, PHP, and modern frameworks.',
      features: ['Dedicated Development Teams', 'Quality Assurance', '24/7 Support', 'Agile Methodologies']
    },
    {
      icon: Wrench,
      title: 'IT Consulting',
      description: 'Strategic IT consulting to help businesses optimize their technology infrastructure and digital transformation.',
      features: ['Technology Strategy', 'Digital Transformation', 'Architecture Design', 'Process Optimization']
    },
    {
      icon: Brain,
      title: 'Software Development',
      description: 'Custom software development solutions tailored to your business needs with cutting-edge technologies.',
      features: ['Custom Applications', 'Web Development', 'Mobile Apps', 'API Integration']
    },
    {
      icon: Database,
      title: 'Building In-house SAAS Products',
      description: 'End-to-end product engineering to build and scale in-house SaaS products from idea to production.',
      features: [
        'HealthTech Solutions',
        'Algo Trading',
        'Product Strategy & Roadmap',
        'MVP to Scale'
      ]
    },
    {
      icon: Cloud,
      title: 'Cloud Engineering',
      description: 'Comprehensive cloud solutions including migration, optimization, and infrastructure management.',
      features: ['Cloud Migration', 'DevOps', 'Infrastructure Setup', 'Cost Optimization']
    }
  ];

  return (
    <section id="services" className="py-20 bg-white relative overflow-hidden">
      {/* Tech Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 right-10 w-96 h-96 border border-cyan-300 rounded-full animate-spin-slow"></div>
        <div className="absolute bottom-10 left-10 w-64 h-64 border border-purple-300 rounded-lg rotate-45 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-128 h-128 border border-cyan-200 rounded-full"></div>
      </div>
      <div className="max-w-8xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-20">
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-8">
            Our <span className="bg-gradient-to-r from-purple-600 via-cyan-500 to-violet-500 bg-clip-text text-transparent animate-pulse">Services</span>
          </h2>
          <p className="text-xl lg:text-2xl text-gray-600 leading-relaxed">
            Comprehensive IT solutions designed to accelerate your business growth and digital transformation journey.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 mb-20">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Card key={index} className="group hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300 border-0 shadow-lg hover:-translate-y-3 hover:border hover:border-cyan-300 transform-gpu perspective-1000 hover:rotate-x-5 bg-white/95 backdrop-blur-sm">
                <CardHeader className="space-y-4">
                  <div className="w-14 h-14 bg-gradient-to-r from-purple-100 via-cyan-50 to-violet-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 group-hover:shadow-lg group-hover:shadow-cyan-500/40 border border-cyan-200/50 group-hover:border-cyan-400/80">
                    <IconComponent className="w-7 h-7 text-purple-600 group-hover:drop-shadow-sm group-hover:text-cyan-600 transition-colors duration-300" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-cyan-500 group-hover:bg-clip-text transition-all duration-300">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-gray-600 mt-2 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-700">
                        <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-cyan-400 rounded-full mr-3 shadow-sm shadow-cyan-500/50"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-2xl p-8 border border-purple-200 shadow-xl shadow-purple-500/10">
            <h3 className="text-2xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-4">
              Ready to Transform Your Business?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Let's discuss how our expert team can help you achieve your technology goals and drive innovation in your industry.
            </p>
            <button 
              className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 shadow-lg shadow-cyan-500/30 hover:shadow-xl hover:shadow-cyan-500/50 transform hover:scale-105 hover:-translate-y-1 border border-cyan-300/50"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Schedule a Consultation
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
